import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import Avatar from '@ad-scripts/presentation/components/common/avatar';
import LinkOutStroke from '@ad-scripts/presentation/components/common/icons/stroke/link-out-stroke';
import PopoverMenu, { PopoverMenuItem, usePopoverMenu } from '@ad-scripts/presentation/components/common/menu';
import agentProfileResource from 'src/actions/agent/agentProfileResource';
import logout from 'src/actions/auth/logout';
import stopImitation from 'src/actions/auth/stopImitation';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import { RootState } from 'src/actions/configuration/rootReducer';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';

const AgentMenu = () => {
  const dispatch = useDispatch();

  const isFeatureEnabled = useIsFeatureEnabled();
  const hasConversationGeneral = isFeatureEnabled('conversation:general');
  const canViewReportOrReportBuilder = isFeatureEnabled('reports:read') || isFeatureEnabled('reportBuilder');

  const canEditProfile = hasConversationGeneral || canViewReportOrReportBuilder;

  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  const agentId = useSelector((state: RootState) => state.auth.userId);
  const isImitatedAgent = !!useSelector((state: RootState) => state.auth.saganUser);
  const { value, loadingState } = useDocumentResource(agentProfileResource, {
    fetch: !!agentId,
    params: { agentId },
  });

  const logoutAgent = () => {
    dispatch(logout());
  };

  const stopAgentImitation = () => {
    dispatch(stopImitation());
  };

  const isLoading = isLoadingStatus(loadingState);

  const { name, avatarUrl } = value;

  return (
    <AgentMenuContainer>
      <AvatarWrapper data-aid="avatar-wrapper" onClick={onToggle} ref={setTargetRef}>
        <Avatar image={avatarUrl} name={name} />
      </AvatarWrapper>
      <>
        {!isLoading && (
          <StyledMenu
            margin={0}
            data-aid={`agentMenu`}
            isOpen={isOpen}
            onClose={onClose}
            position="bottom"
            targetPosition="start"
            targetRef={targetRef}
          >
            {canEditProfile && (
              <StyledLink href="/user/profile">
                <MenuItem data-aid="menuItem-Profile">Profile</MenuItem>
              </StyledLink>
            )}
            <StyledLink href="/support.html" target="_blank">
              <MenuItem data-aid="menuItem-Help">
                Help <StyledLinkOutStroke />
              </MenuItem>
            </StyledLink>

            {isImitatedAgent ? (
              <MenuItemLast onClick={stopAgentImitation}>Stop Impersonation</MenuItemLast>
            ) : (
              <MenuItemLast data-aid="menuItem-Logout" onClick={logoutAgent}>
                Log out
              </MenuItemLast>
            )}
          </StyledMenu>
        )}
      </>
    </AgentMenuContainer>
  );
};

export const AgentMenuContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-right: ${p => p.theme.spacing.inlineLarge};
`;

const AvatarWrapper = styled.div`
  cursor: pointer;
`;

export const StyledLinkOutStroke = styled(LinkOutStroke)`
  height: 14px;
  width: 14px;
  margin-left: 4px;
  fill: ${p => p.theme.colors.gray600};
`;

const StyledLink = styled.a`
  color: ${p => p.theme.colors.black};
  text-decoration: none;
  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: none;
  }
  &:visited {
    color: ${p => p.theme.colors.black};
    text-decoration: none;
  }
`;

const StyledMenuItem = styled(PopoverMenuItem)`
  padding: 8px 24px;
  &:hover {
    background: ${p => p.theme.colors.green400};
    ${StyledLink} {
      color: ${p => p.theme.colors.white};
    }
  }
`;

const MenuItem = styled(PopoverMenuItem)`
  height: 32px;
  line-height: 162%;
  padding-top: 5px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-content: flex-start;
  min-width: 240px;

  &:first-child {
    height: 36px;
    padding-top: 9px;
  }

  &:hover {
    background: ${p => p.theme.colors.green100};
  }
`;

const MenuItemLast = styled(PopoverMenuItem)`
  height: 36px;
  line-height: 162%;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-content: flex-start;
  min-width: 240px;
  padding-bottom: 12px;
  &:hover {
    background: ${p => p.theme.colors.green100};
  }
`;

const StyledMenu = styled(PopoverMenu)`
  left: -15px;
  min-width: 150px;

  border-radius: 8px;
  margin-top: 0;

  ${StyledMenuItem}:first-child {
    padding: 16px 24px 8px 24px;
  }
`;

export default AgentMenu;
